import React from "react";
import styled, { keyframes } from "styled-components";
import { narrowScreenMediaQuery, outerContainerCss } from "../../constants/styles";

// Static list of partner-company logos rendered in the "Dipercaya oleh
// Perusahaan Ternama" marquee. These are real brand assets the legacy
// homepage already uses (see `app/views/homes/index.html.erb` lines 80-116).
// Admins don't manage this list through the CMS — a code change is the
// intended editing path, so the array lives inline with the component.
const PARTNER_LOGOS = [
  { id: "co-1", name: "Antam",      image: "https://d2yxyrkr28gmlk.cloudfront.net/wp-content/uploads/2021/10/Antam-logo.png" },
  { id: "co-2", name: "Inalum",     image: "https://d2yxyrkr28gmlk.cloudfront.net/wp-content/uploads/2021/10/Inalum-Logo.png" },
  { id: "co-3", name: "Pertamina",  image: "https://d2yxyrkr28gmlk.cloudfront.net/wp-content/uploads/2021/10/Pertamina-Logo.png" },
  { id: "co-4", name: "WiKA",       image: "https://d2yxyrkr28gmlk.cloudfront.net/wp-content/uploads/2021/10/wika-logo.png" },
  { id: "co-5", name: "Bukit Asam", image: "/assets/company/Bukit%20Asam%20Logo.png" },
  { id: "co-6", name: "Pelindo",    image: "/assets/company/Logo%20Pelindo%20Gateway.png" },
  { id: "co-7", name: "Timah",      image: "/assets/company/logo-timah.png" },
  { id: "co-8", name: "Bank Mandiri", image: "/assets/company/mgr-Logo-Bank-Mandiri-1.png" },
];

// Infinite marquee: duplicate the array + translate the whole track by
// exactly one set width; loop restart is invisible because the second set
// already matches the starting position.

const Wrap = styled.section`
  background: #ffffff;
  padding: 56px 0 72px;
  overflow: hidden;

  @media ${narrowScreenMediaQuery} { padding: 32px 0 48px; }

  &[data-theme="dark"], [data-theme="dark"] & {
    background: #0f172a;
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
  text-align: center;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.01em;

  @media ${narrowScreenMediaQuery} { font-size: 20px; }

  &[data-theme="dark"], [data-theme="dark"] & { color: #f3f4f6; }
`;

const Subtitle = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 40px;
  letter-spacing: -0.01em;

  @media ${narrowScreenMediaQuery} { font-size: 20px; margin-bottom: 24px; }

  &[data-theme="dark"], [data-theme="dark"] & { color: #f3f4f6; }
`;

const marquee = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const MarqueeTrack = styled.div`
  display: flex;
  gap: 64px;
  align-items: center;
  width: max-content;
  /* Each logo gets roughly 3s of screen time regardless of logo count,
   * so the marquee speed scales with the number of brands. */
  animation: ${marquee} ${({ $durationSec }) => $durationSec}s linear infinite;

  /* Pause on hover so users can linger on a logo */
  &:hover {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation-play-state: paused;
  }
`;

const MarqueeViewport = styled.div`
  position: relative;
  overflow: hidden;
  mask-image: linear-gradient(
    90deg,
    transparent 0,
    #000 60px,
    #000 calc(100% - 60px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent 0,
    #000 60px,
    #000 calc(100% - 60px),
    transparent 100%
  );
`;

const Logo = styled.img`
  height: 64px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
  filter: grayscale(100%) opacity(0.65);
  transition: filter 250ms ease;
  flex-shrink: 0;

  ${MarqueeTrack}:hover & {
    filter: grayscale(0%) opacity(1);
  }

  @media ${narrowScreenMediaQuery} { height: 48px; }

  &[data-theme="dark"], [data-theme="dark"] & {
    filter: grayscale(100%) opacity(0.45) invert(0.85);

    ${MarqueeTrack}:hover & {
      filter: grayscale(0%) opacity(1) invert(0);
    }
  }
`;

// Seconds per logo for on-screen dwell. Tweak this single knob to make
// the marquee faster/slower regardless of how many brands ship.
const SECONDS_PER_LOGO = 3;

function CompanyLogos() {
  const count = PARTNER_LOGOS.length;
  // Duplicate the array for seamless looping. Duration scales linearly so
  // each brand gets ~SECONDS_PER_LOGO regardless of the total count.
  const logos = React.useMemo(
    () => [...PARTNER_LOGOS, ...PARTNER_LOGOS],
    [],
  );
  const durationSec = Math.max(10, count * SECONDS_PER_LOGO);

  return (
    <Wrap>
      <Inner>
        <Title>Dipercaya oleh Perusahaan Ternama</Title>
        <Subtitle>Sebagai Media Pembelajaran</Subtitle>
      </Inner>
      <MarqueeViewport>
        <MarqueeTrack $durationSec={durationSec}>
          {logos.map((c, i) => (
            <Logo
              key={`${c.id}-${i}`}
              src={c.image}
              alt={c.name}
              loading="lazy"
              decoding="async"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ))}
        </MarqueeTrack>
      </MarqueeViewport>
    </Wrap>
  );
}

export default React.memo(CompanyLogos);
